import React, { useEffect, useRef, useState } from "react";
import { API_BASE } from "../../api/config";
import DropboxOrdnerWahl from "../../components/DropboxOrdnerWahl";
import GeminiWarnung from "../../components/GeminiWarnung";

/**
 * Ordner-Batch (Welle 100): Dropbox-Ordner wählen, ganz oder nur angehakte PDFs,
 * Ziel = gleicher Ordner oder anderer Ordner. Läuft durch den normalen Rename-me-Batch.
 */
const API = `${API_BASE}/rename-me-ocr/api`;
const START = "/Holthey Immobilien/--- scan - Ablage";

const merke = (k, v) => { try { localStorage.setItem(k, v); } catch { /* privat/blockiert */ } };
const gemerkt = (k, d) => { try { return localStorage.getItem(k) || d; } catch { return d; } };

const STATUS = {
  fertig: { text: "umbenannt", klasse: "ok" },
  nok: { text: "prüfen", klasse: "nok" },
  fehler: { text: "Fehler", klasse: "fehler" },
  uebersprungen: { text: "übersprungen", klasse: "still" },
};

export default function OrdnerBatchDialog({ onSchliessen }) {
  const [schritt, setSchritt] = useState("quelle");   // quelle → ziel → lauf
  const [quelle, setQuelle] = useState(() => gemerkt("rename_ordner_quelle", START));
  const [pdfs, setPdfs] = useState([]);
  const [auswahl, setAuswahl] = useState(new Set());
  const [gleich, setGleich] = useState(true);
  const [ziel, setZiel] = useState(() => gemerkt("rename_ordner_ziel", START));
  const [job, setJob] = useState(null);
  const [fehler, setFehler] = useState("");
  const [startet, setStartet] = useState(false);
  const poll = useRef(null);

  useEffect(() => () => clearInterval(poll.current), []);

  const laeuft = job && job.status === "laeuft";
  const pdfListe = pdfs || [];   // null = Ordner lädt noch
  const alle = pdfListe.length > 0 && auswahl.size === pdfListe.length;

  const starten = async () => {
    setFehler(""); setStartet(true);
    merke("rename_ordner_quelle", quelle);
    if (!gleich) merke("rename_ordner_ziel", ziel);
    try {
      const r = await fetch(`${API}/ordner-batch/start`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          quelle, ziel: gleich ? null : ziel,
          // alle angehakt = ganzer Ordner (auch was bis zum Start noch dazukommt)
          dateien: alle ? null : [...auswahl],
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setJob(d); setSchritt("lauf");
      poll.current = setInterval(async () => {
        try {
          const s = await fetch(`${API}/ordner-batch/status/${d.id}`).then((x) => x.json());
          setJob(s);
          if (s.status !== "laeuft") clearInterval(poll.current);
        } catch { /* nächster Versuch */ }
      }, 2000);
    } catch (e) {
      setFehler(String(e.message || e));
    } finally {
      setStartet(false);
    }
  };

  const stoppen = async () => {
    if (!job) return;
    try { setJob(await fetch(`${API}/ordner-batch/stop/${job.id}`, { method: "POST" }).then((x) => x.json())); }
    catch { /* Status-Abfrage zeigt es ohnehin */ }
  };

  const erledigt = job ? job.dateien.length : 0;
  // ganzer Ordner: das Backend kennt die Zahl erst am Ende — bis dahin die gezählten PDFs
  const gesamt = job ? (job.gesamt || pdfListe.length || 0) : 0;
  const prozent = gesamt ? Math.min(100, Math.round((erledigt / gesamt) * 100)) : 0;
  const zaehle = (st) => (job ? job.dateien.filter((d) => d.status === st).length : 0);
  const kurz = (p) => String(p || "").replace(/^\/Holthey Immobilien\/?/, "") || "/";

  return (
    <div className="du-modal-hg" onMouseDown={(e) => { if (e.target === e.currentTarget && !laeuft) onSchliessen(); }}>
      <div className="du-modal" role="dialog" aria-modal="true" aria-label="Ordner umbenennen">
        <div className="du-modal-kopf">
          <div>
            <h3>Ordner umbenennen</h3>
            <p>Gleiche Erkennung wie der Rename-me-Batch (v1 + Gemini). Nichts wird gelöscht.</p>
          </div>
          <button type="button" className="du-x" onClick={onSchliessen} disabled={laeuft} title="Schließen">×</button>
        </div>

        <GeminiWarnung wofuer="Der Ordner-Batch startet nicht, bis Gemini wieder geht — sonst würde alles nur mit v1 benannt." />

        <ol className="du-schritte">
          <li className={schritt === "quelle" ? "aktiv" : "fertig"}>1 · Dateien wählen</li>
          <li className={schritt === "ziel" ? "aktiv" : schritt === "lauf" ? "fertig" : ""}>2 · Ziel wählen</li>
          <li className={schritt === "lauf" ? "aktiv" : ""}>3 · Umbenennen</li>
        </ol>

        {schritt !== "lauf" && (
          // bleibt eingehängt (nur ausgeblendet), damit die Auswahl beim „Zurück" erhalten bleibt
          <div style={{ display: schritt === "quelle" ? "block" : "none" }}>
            <DropboxOrdnerWahl pfad={quelle} onPfad={setQuelle} mitDateien
              auswahl={auswahl} onAuswahl={setAuswahl} onPdfs={setPdfs} />
            <div className="du-auswahl-zeile">
              <span>{pdfs === null ? "Ordner wird gelesen …"
                : pdfListe.length ? `${auswahl.size} von ${pdfListe.length} PDFs ausgewählt${alle ? " — ganzer Ordner" : ""}`
                  : "In diesem Ordner liegen keine PDFs."}</span>
              <span className="du-auswahl-knoepfe">
                <button type="button" className="du-btn text" onClick={() => setAuswahl(new Set(pdfListe))} disabled={!pdfListe.length || alle}>Alle</button>
                <button type="button" className="du-btn text" onClick={() => setAuswahl(new Set())} disabled={!auswahl.size}>Keine</button>
              </span>
            </div>
            <div className="du-modal-fuss">
              <button type="button" className="du-btn neben" onClick={onSchliessen}>Abbrechen</button>
              <button type="button" className="du-btn haupt" onClick={() => setSchritt("ziel")} disabled={!auswahl.size || pdfs === null}>Weiter</button>
            </div>
          </div>
        )}

        {schritt === "ziel" && (
          <>
            <div className="du-ziel-wahl">
              <label className={gleich ? "an" : ""}>
                <input type="radio" checked={gleich} onChange={() => setGleich(true)} />
                <span><b>Im gleichen Ordner umbenennen</b><small>Die Dateien bleiben in „{kurz(quelle)}", nur der Name ändert sich.</small></span>
              </label>
              <label className={!gleich ? "an" : ""}>
                <input type="radio" checked={!gleich} onChange={() => setGleich(false)} />
                <span><b>Nach dem Umbenennen verschieben</b><small>Die umbenannten Dateien wandern in einen anderen Ordner.</small></span>
              </label>
            </div>
            {!gleich && <DropboxOrdnerWahl pfad={ziel} onPfad={setZiel} neuerOrdner />}
            <div className="du-zusammenfassung">
              {alle ? `Ganzer Ordner (${pdfListe.length} PDFs)` : `${auswahl.size} ausgewählte PDF${auswahl.size === 1 ? "" : "s"}`}
              {" "}aus „{kurz(quelle)}" → {gleich ? "bleiben dort" : `nach „${kurz(ziel)}"`}
            </div>
            {fehler && <div className="du-meldung fehler">{fehler}</div>}
            <div className="du-modal-fuss">
              <button type="button" className="du-btn neben" onClick={() => setSchritt("quelle")}>Zurück</button>
              <button type="button" className="du-btn haupt" onClick={starten}
                disabled={startet || (!gleich && (!ziel || ziel.toLowerCase() === quelle.toLowerCase()))}>
                {startet ? "Startet …" : "Umbenennen starten"}
              </button>
            </div>
          </>
        )}

        {schritt === "lauf" && job && (
          <>
            <div className="du-fortschritt">
              <div className="du-balken"><div style={{ width: `${laeuft ? prozent : 100}%` }} /></div>
              <div className="du-fortschritt-text">
                {laeuft
                  ? (job.stop_angefordert ? "Hält nach der aktuellen Datei an …"
                    : `${erledigt} von ${gesamt || "?"} — ${job.aktuell ? `gerade: ${job.aktuell}` : "startet …"}`)
                  : job.status === "fehler" ? `Abgebrochen: ${job.fehler}`
                    : `${job.status === "gestoppt" ? "Angehalten" : "Fertig"}: ${zaehle("fertig")} umbenannt`
                      + `${zaehle("nok") ? `, ${zaehle("nok")} zum Prüfen` : ""}`
                      + `${zaehle("fehler") ? `, ${zaehle("fehler")} Fehler` : ""}`
                      + `${zaehle("uebersprungen") ? `, ${zaehle("uebersprungen")} übersprungen` : ""}`}
              </div>
            </div>
            <div className="du-lauf-liste">
              {!job.dateien.length && <div className="du-leer">Die erste Datei wird ausgewertet — das dauert je Datei etwa 20–60 Sekunden.</div>}
              {job.dateien.map((d, i) => {
                const st = STATUS[d.status] || { text: d.status, klasse: "still" };
                return (
                  <div key={i} className="du-lauf-zeile">
                    <span className={`du-status ${st.klasse}`}>{st.text}</span>
                    <div className="du-lauf-namen">
                      <div className="alt" title={d.name}>{d.name}</div>
                      {d.neu && <div className="neu" title={d.neu}>→ {d.neu}</div>}
                      {d.grund && d.status !== "fertig" && <div className="grund">{d.grund}</div>}
                    </div>
                    {d.engine && <span className="du-engine">{d.engine === "gemini" ? "Gemini" : "v1"}</span>}
                  </div>
                );
              })}
            </div>
            <div className="du-modal-fuss">
              {laeuft
                ? <button type="button" className="du-btn gefahr" onClick={stoppen} disabled={job.stop_angefordert}>Stopp</button>
                : <button type="button" className="du-btn haupt" onClick={onSchliessen}>Schließen</button>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
